export type LoginWelcomeInput = {
  email: string;
  firstName?: string | null;
  roles?: string[] | undefined;
  profilePictureUrl?: string | null;
};

const ROLE_QUIPS: Record<string, string[]> = {
  SUPER_ADMIN: [
    'Toute la pharmacie vous attend.',
    'Les clés du royaume sont entre de bonnes mains.',
  ],
  CATALOG: [
    'Le catalogue n’attend que vous.',
    'Les SKU tremblent déjà.',
  ],
  WAREHOUSE: [
    'Les lots ne comptent pas seuls.',
    'Le stock a besoin de vos yeux.',
  ],
  BUYER: [
    'Les fournisseurs vous attendent.',
    'Un bon de commande de plus ?',
  ],
  SALES: [
    'Prêt à facturer ?',
    'Les commandes ne se valident pas toutes seules.',
  ],
  VIEWER: [
    'Mode zen activé — lecture seule.',
    'Vous regardez, le monde tourne.',
  ],
};

const FALLBACK_QUIPS = [
  'Heureux de vous revoir.',
  'Bon retour parmi nous.',
  'On vous garde une place au chaud.',
  'Encore un beau jour à la pharmacie.',
  'Les ordonnances peuvent attendre — vous, non.',
];

const ROLE_PRIORITY = [
  'SUPER_ADMIN',
  'CATALOG',
  'BUYER',
  'WAREHOUSE',
  'ACCOUNTANT',
  'SALES',
  'VIEWER',
] as const;

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function pickStable<T>(items: readonly T[], seed: string): T {
  return items[hashString(seed) % items.length]!;
}

function getTimeGreeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour >= 18) return 'Bonsoir';
  if (hour >= 12) return 'Bon après-midi';
  return 'Bonjour';
}

function displayNameFromIdentity({ email, firstName }: LoginWelcomeInput): string {
  const trimmed = firstName?.trim();
  if (trimmed) return trimmed;
  const local = email.split('@')[0]?.trim();
  if (!local) return email;
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function primaryRole(roles: string[] | undefined): string | undefined {
  const list = roles ?? [];
  for (const role of ROLE_PRIORITY) {
    if (list.includes(role)) return role;
  }
  return list[0];
}

function getRoleQuip(roles: string[] | undefined, seed: string): string {
  const role = primaryRole(roles);
  if (role && ROLE_QUIPS[role]) {
    return pickStable(ROLE_QUIPS[role], `${seed}:${role}`);
  }
  return pickStable(FALLBACK_QUIPS, seed);
}

export function buildLoginWelcome(identity: LoginWelcomeInput, now = new Date()) {
  const displayName = displayNameFromIdentity(identity);
  const greetingLead = getTimeGreeting(now);
  const greeting = `${greetingLead}, ${displayName}`;
  const quip = getRoleQuip(identity.roles, identity.email.toLowerCase());

  return {
    greeting,
    greetingLead,
    emphasizedName: displayName,
    displayName,
    quip,
  };
}
